import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PhoneIcon, MenuIcon, XIcon } from './IconComponents';
import BRAND from '../brand';

const BrandLogo: React.FC<{ className?: string }> = ({ className }) => (
  <img
    src={BRAND.logo}
    alt={`${BRAND.name} Logo`}
    className={className}
  />
);

const NAV_ITEMS = [
  { name: 'Services', to: '/services' },
  { name: 'About', to: '/about' },
  { name: 'Locations', to: '/locations'},
  { name: 'Process', to: '/', scrollTo: 'process' }, // Anchor link on home
  { name: 'Careers', to: '/careers' },
  { name: 'Admin', to: '/admin' },
];

const NavLinks: React.FC<{ className?: string, onLinkClick?: () => void }> = ({ className, onLinkClick }) => (
  <nav className={className}>
    {NAV_ITEMS.map((item) => (
      <Link
        key={item.name}
        to={item.to}
        onClick={() => {
          if (item.scrollTo) {
            setTimeout(() => {
              const element = document.getElementById(item.scrollTo);
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }
          onLinkClick?.();
        }}
        className="block sm:inline-block text-lg sm:text-base font-semibold text-neutral hover:text-primary transition-colors py-2 sm:py-0 sm:px-4"
      >
        {item.name}
      </Link>
    ))}
  </nav>
);

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled || isMenuOpen ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <Link to="/" className="flex-shrink-0 flex items-center h-12 sm:h-14">
            <BrandLogo className="h-full w-auto transition-all duration-300" />
          </Link>

          <div className="hidden lg:flex items-center space-x-2">
            <NavLinks />
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <a href={BRAND.phoneHref} className="flex items-center gap-2 font-semibold text-neutral hover:text-primary transition-colors">
              <PhoneIcon className="h-5 w-5" />
              {BRAND.phone}
            </a>
            <Link to="/contact" className="px-5 py-3 bg-primary text-primary-content font-bold rounded-lg shadow-md hover:bg-primary-focus transition-colors">
              Get a Free Estimate
            </Link>
          </div>

          <div className="lg:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Open menu">
              <MenuIcon className="h-8 w-8 text-neutral" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden fixed inset-0 bg-white z-[60] transform ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'} transition-transform duration-300 ease-in-out`}>
        <div className="flex justify-between items-center py-3 px-4 border-b">
           <Link to="/" onClick={closeMenu}>
             <BrandLogo className="h-auto w-48" />
           </Link>
          <button onClick={closeMenu} aria-label="Close menu">
            <XIcon className="h-8 w-8 text-neutral" />
          </button>
        </div>
        <div className="p-8 space-y-4">
            <NavLinks className="flex flex-col space-y-4" onLinkClick={closeMenu} />
            <Link
              to="/contact"
              onClick={closeMenu}
              className="w-full mt-6 block text-center px-6 py-4 bg-primary text-primary-content font-bold rounded-lg shadow-md hover:bg-primary-focus transition-colors"
            >
              Get a Free Estimate
            </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;
